import { useCallback } from "react";

import { useHistory, useParams } from "react-router";
import {
  ContractTypeAws,
  ContractTypeBettercloud,
  ContractTypeGoogleCloudPartnerLedPremiumSupport,
  ContractTypeZendesk,
  InternalTierNames,
  TierPackageTypes,
} from "@doitintl/cmp-models";
import BackIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  IconButton,
  MobileStepper,
  Paper,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { useApiContext } from "../../../api/context";
import LoadingButton from "../../../Components/LoadingButton";
import { useSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { pickerContext, PickerContextProvider } from "../../../Context/GapiContext";
import { useTier } from "../../../Context/TierProvider";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { consoleErrorWithSentry } from "../../../utils";
import { useFullScreen } from "../../../utils/dialog";
import { useContractTypesContext } from "../../ContractsTabs/ContractsTypesContext";
import { useTurnOffPresentationMode } from "../api";
import { ContractFormContextProvider, useContractFormContext } from "./ContractsFormContext";
import { getStepsConfig } from "./Steps/StepsConfig";
import { type VendorContractRequestPayload } from "./types";
import {
  contractApiFormattedPayload,
  formatContractToFs,
  formatVendorContractToFs,
  isAmazonWebServices,
  isDoitCloudProduct,
  vendorContractApiFormattedPayload,
} from "./utils";

export const ContractForm = () => {
  const history = useHistory();
  const snackbar = useSnackbar();
  const { customer, entities, assets } = useCustomerContext();
  const { customerId, contractId } = useParams<{ customerId: string; contractId: string }>();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const api = useApiContext();
  const { isMobile } = useFullScreen();
  const { getInternalTiers } = useTier();
  const turnOffPresentationMode = useTurnOffPresentationMode(api);
  const { state, setState } = useContractFormContext();
  const handleBackToList = useCallback(() => {
    history.push(`/customers/${customerId}/billing/contracts`);
  }, [customerId, history]);
  const stepsConfig = getStepsConfig(state);
  const { loading: contractTypesLoading } = useContractTypesContext();

  const turnOffPresentationModeOnContractCreate = async () => {
    const tiers = getInternalTiers(TierPackageTypes.NAVIGATOR);

    if (isCustomerInPresentationMode(customer)) {
      await turnOffPresentationMode(customer.id, {
        navigatorTierId: tiers?.[InternalTierNames.ZERO_ENTITLEMENTS]?.id,
      });
    }
  };

  const updateAWSPricebooks = () => {
    if (!isAmazonWebServices(state)) {
      return [];
    }
    const { selectedPricebooks } = state;

    return Object.entries(selectedPricebooks)
      .map(([payer_id, price_book_id]) => ({
        price_book_id,
        customer_id: customerId,
        payer_id,
        contract_id: contractId,
      }))
      .map((payload) => api.post("/v1/pricebooks/amazon-web-services/assignments", payload));
  };

  const createUpdateContractWithAPI = async () => {
    const endpoint = state.editMode ? `update/${contractId}` : "create";
    const contract = formatContractToFs(state, customer, currentUser, assets);
    const contractPayload = contractApiFormattedPayload(contract);

    let vendorContractPayload: VendorContractRequestPayload | null = null;

    if (state.vendorContract?.commitmentType) {
      const vendorContract = formatVendorContractToFs(state, customer, currentUser, assets);
      vendorContractPayload = vendorContractApiFormattedPayload(vendorContract);
    }

    return api.post(`/v1/contract/${endpoint}`, {
      contract: contractPayload,
      vendorContract: vendorContractPayload,
    });
  };

  const handleSubmit = async () => {
    if (!stepsConfig[state.activeStep]?.isValid(state, setState)) {
      return;
    }

    setState({ ...state, loading: true });

    try {
      if (state.type === ContractTypeAws) {
        await Promise.all(updateAWSPricebooks()).catch((err) => {
          throw new Error(`pricebook assignment error (${err.response?.data?.errors})`);
        });
      }

      await createUpdateContractWithAPI();

      if (!state.editMode && !isDoitCloudProduct(state.type)) {
        await turnOffPresentationModeOnContractCreate();
      }

      if (state.type === ContractTypeGoogleCloudPartnerLedPremiumSupport) {
        api.put(`/support/customers/${customerId}/add-plps-private-message`);
      }

      snackbar.onOpen({
        message: "Contract saved successfully",
        variant: "success",
        autoHideDuration: 5000,
        withClose: true,
      });

      setState({ ...state, loading: false });

      if ([ContractTypeZendesk, ContractTypeBettercloud].includes(state.type)) {
        history.push(`/customers/${customerId}/integrate/assets/${state.type}/create`);
        return;
      }
      handleBackToList();
    } catch (error: any) {
      consoleErrorWithSentry(error);
      let errorMessage = "Failed to save contract.";
      if (error?.response?.data) {
        errorMessage += ` Error: ${error.response.data}. Status: ${error.response.status}`;
      } else if (error?.message) {
        errorMessage += ` Error: ${error.message}`;
      }
      setState({ ...state, loading: false });
      snackbar.onOpen({
        message: errorMessage,
        variant: "error",
        withClose: true,
      });
    }
  };

  const steps = [...stepsConfig.map((step) => step.title)];

  const handleNext = () => {
    if (stepsConfig[state.activeStep]?.isValid(state, setState)) {
      let nextStep = state.activeStep + 1;

      while (stepsConfig[nextStep]?.skip?.()) {
        nextStep += 1;
      }

      setState({ ...state, activeStep: nextStep });
    }
  };

  const handleBack = () => {
    let newStep = state.activeStep - 1;
    while (stepsConfig[newStep]?.skip?.()) {
      newStep -= 1;
    }

    setState({ ...state, activeStep: newStep });
  };

  const { activeStep, loading } = state;

  if ((contractId && !state.editMode) || contractTypesLoading) {
    return null;
  }

  return (
    <>
      <Card sx={{ border: "none", boxShadow: "none", paddingTop: 4, paddingBottom: 8 }} data-cy="contract-form">
        {isMobile && (
          <CardHeader
            avatar={
              <IconButton aria-label="Back" onClick={handleBackToList} size="large">
                <BackIcon color="primary" />
              </IconButton>
            }
            title={state.editMode ? "Edit Contract" : "Create a Contract"}
            subheader={customer.name}
          />
        )}
        {Boolean(entities) && (
          <CardContent>
            {!isMobile && (
              <Container maxWidth="lg">
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label, index) => (
                    <Step
                      key={label}
                      completed={activeStep > index && !stepsConfig[index]?.skip?.()}
                      disabled={stepsConfig[index]?.skip?.()}
                    >
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Container>
            )}
            <Box sx={{ pt: 3 }}>{stepsConfig[activeStep]?.component}</Box>
          </CardContent>
        )}
        {isMobile && (
          <MobileStepper
            steps={steps.length}
            position="bottom"
            activeStep={activeStep}
            variant="progress"
            nextButton={
              activeStep >= steps.length - 1 ? (
                <LoadingButton
                  color="primary"
                  size="small"
                  onClick={handleSubmit}
                  data-cy="submit-button"
                  disabled={loading || state.isEditForbidden}
                  loading={loading}
                  mixpanelEventId="contracts.submit"
                >
                  Submit
                  <KeyboardArrowRight />
                </LoadingButton>
              ) : (
                <Button
                  color="primary"
                  size="small"
                  data-cy="next-button"
                  onClick={handleNext}
                  disabled={stepsConfig.length <= 2}
                >
                  Next
                  <KeyboardArrowRight />
                </Button>
              )
            }
            backButton={
              <Button
                color="primary"
                size="small"
                data-cy="back-button"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                <KeyboardArrowLeft />
                Back
              </Button>
            }
          />
        )}
      </Card>
      {!isMobile && (
        <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1 }}>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Divider />
            </Grid>

            <Grid size={5}>
              <CardActions>
                <Button
                  color="primary"
                  variant="outlined"
                  data-cy="back-button"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </CardActions>
            </Grid>
            <Grid>
              <CardActions>
                <Button
                  sx={{ mx: 1 }}
                  color="primary"
                  aria-label="cancel"
                  data-cy="cancel-button"
                  onClick={handleBackToList}
                >
                  Cancel
                </Button>
                {activeStep >= steps.length - 1 ? (
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    data-cy="submit-button"
                    onClick={handleSubmit}
                    disabled={loading || state.isEditForbidden}
                    loading={loading}
                    mixpanelEventId="contracts.submit"
                  >
                    Submit
                  </LoadingButton>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    data-cy="next-button"
                    onClick={handleNext}
                    disabled={stepsConfig.length <= 2}
                  >
                    Next
                  </Button>
                )}
              </CardActions>
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
};

const ContractFormWrapper = (props) => (
  <PickerContextProvider>
    <pickerContext.Consumer>
      {({ pickerApiLoaded }) => (
        <ContractFormContextProvider>
          <ContractForm pickerApiLoaded={pickerApiLoaded} {...props} />
        </ContractFormContextProvider>
      )}
    </pickerContext.Consumer>
  </PickerContextProvider>
);

export default ContractFormWrapper;
